/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { euiFlexItemStyles } from './flex_item.styles';
export var EuiFlexItem = ({
  children,
  className,
  grow = true,
  component: Component = 'div',
  ...rest
}) => {
  validateGrowValue(grow);
  const styles = euiFlexItemStyles();
  const cssStyles = [styles.euiFlexItem, !grow ? styles.growZero : styles.grow, grow && (typeof grow === 'number' ? styles.growSizes[grow] : styles.growSizes['1'])];
  const classes = classNames('euiFlexItem', className);
  return <Component css={cssStyles} className={classes} {...rest}>
      {children}
    </Component>;
};
EuiFlexItem.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  grow: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, null])]),
  // Leave this as an inline string enum so the props table properly parses it
  /**
     * @default div
     */
  component: PropTypes.any
};
export const VALID_GROW_VALUES = [null, undefined, true, false, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
function validateGrowValue(value) {
  if (VALID_GROW_VALUES.indexOf(value) === -1) {
    throw new Error(`Prop \`grow\` passed to \`EuiFlexItem\` must be a boolean or an integer between 0 and 10, received \`${value}\``);
  }
}
try {
  EuiFlexItem.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/flex/flex_item.tsx',
    description: '',
    displayName: 'EuiFlexItem',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      grow: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'grow',
        parent: {
          fileName: 'app/src/components/flex/flex_item.tsx',
          name: 'EuiFlexItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_item.tsx',
          name: 'EuiFlexItemProps'
        }],
        required: false,
        type: {
          name: 'boolean | 0 | 6 | 3 | 2 | 1 | 10 | 4 | 7 | 8 | 5 | 9'
        }
      },
      component: {
        defaultValue: {
          value: 'div'
        },
        description: '',
        name: 'component',
        parent: {
          fileName: 'app/src/components/flex/flex_item.tsx',
          name: 'EuiFlexItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_item.tsx',
          name: 'EuiFlexItemProps'
        }],
        required: false,
        type: {
          name: 'ElementType<any>'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiFlexItemProps']
  };
} catch (e) {}